import { inject, Injectable, signal } from '@angular/core';
import { assign } from 'lodash-es';
import { switchMap, tap } from 'rxjs';
import { SignalingBlock, SignalingBlockCreateRequest } from '../client-signaling.model';
import { ClientSignalingService } from './client-signaling.service';

@Injectable({
  providedIn: 'root',
})
export class SignalingBlockStoreService {
  private readonly _scm = inject(ClientSignalingService);
  private readonly _blocks = signal<SignalingBlock[]>([]);

  public readonly blocks = this._blocks.asReadonly();

  loadBlocks(clientId: string) {
    return this._scm.getAll(clientId).pipe(tap((result) => this._blocks.set(result)));
  }

  addBlock(clientId: string, data: SignalingBlockCreateRequest) {
    return this._scm.create(clientId, data).pipe(
      switchMap((blockId) => this._scm.get(clientId, blockId)),
      tap((result) => this._blocks.update((currentState) => [...currentState, result]))
    );
  }

  removeBlock(clientId: string, blockId: string) {
    return this._scm.delete(clientId, blockId).pipe(
      tap(() => {
        this._blocks.update((currentState) =>
          currentState.filter((value) => value._id != blockId)
        );
      })
    );
  }

  updateActive(clientId: string, blockId: string, data: { active: boolean }) {
    return this._scm.updateActive(clientId, blockId, data).pipe(
      tap(() =>
        this._blocks.update((currentState) =>
          currentState.map((value) => {
            if (value._id === blockId) {
              return assign(value, data);
            }

            return value;
          })
        )
      )
    );
  }

  updateAvatar(clientId: string, blockId: string, data: { fileId: string }) {
    return this._scm.updateAvatar(clientId, blockId, data).pipe(
      switchMap(() => this._scm.get(clientId, blockId)),
      tap((result) => {
        this._blocks.update((currentState) =>
          currentState.map((value) => {
            if (value._id === blockId) {
              return assign(value, result);
            }

            return value;
          })
        );
      })
    );
  }

  updateBlockConfig(clientId: string, blockId: string, updateConfig: unknown) {
    return this._scm.updateConfig(clientId, blockId, updateConfig).pipe(
      switchMap(() => this._scm.get(clientId, blockId)),
      tap((result) =>
        this._blocks.update((currentState) =>
          currentState.map((value) => {
            if (value._id === blockId) {
              return assign(value, result);
            }

            return value;
          })
        )
      )
    );
  }

  addReference(clientId: string, blockId: string, data: { referenceIds: string[] }) {
    return this._scm.addReference(clientId, blockId, data).pipe(
      switchMap(() => this._scm.get(clientId, blockId)),
      tap((result) =>
        this._blocks.update((currentState) =>
          currentState.map((value) => {
            if (value._id === blockId) {
              return assign(value, result);
            }

            return value;
          })
        )
      )
    );
  }

  deleteReference(clientId: string, blockId: string, linkId: string) {
    return this._scm.deleteReference(clientId, blockId, linkId).pipe(
      switchMap(() => this._scm.get(clientId, blockId)),
      tap((result) =>
        this._blocks.update((currentState) =>
          currentState.map((value) => {
            if (value._id === blockId) {
              return assign(value, result);
            }

            return value;
          })
        )
      )
    );
  }

  clearBlocks(): void {
    this._blocks.set([]);
  }
}
